import { boundedRequestSignal, handleApiResponse } from './auth-fetch';
import { errorFromHttpResponse } from './error-presentation';
import { authenticatedCacheGeneration, requireAuthenticatedGeneration } from '@/shared/lib/authenticated-session';

const DEFAULT_TIMEOUT_MS = 30_000;

// A 401 from any API call means the session gate rejected us (expired/lost
// cookie). Signal the AuthGate to show the login screen instead of surfacing
// a raw error toast.
/**
 * ОТКАЗ ПО ТАЙМ-АУТУ ОБЯЗАН НАЗЫВАТЬ ПРИЧИНУ СЛОВАМИ.
 *
 * `boundedRequestSignal` рвёт запрос `DOMException('request deadline')`, и это
 * сообщение уходило человеку как есть: в «Конструкторе кухни» красной строкой
 * стояло ровно «request deadline» — ни какой запрос, ни сколько ждали, ни что
 * делать. Машинный код остаётся, но под своей подписью, а первым идёт текст.
 */
function humanDeadlineRefusal(error: unknown, url: string, method: string, timeoutMs: number): unknown {
  const isDeadline = typeof DOMException !== 'undefined'
    && error instanceof DOMException
    && error.name === 'TimeoutError'
    && error.message === 'request deadline';
  if (!isDeadline) return error;
  const seconds = Math.round(timeoutMs / 100) / 10;
  return new Error(
    `Браузер прервал запрос ${method} ${url}: сервер не ответил за ${seconds} с. `
    + 'Ответ мог быть уже в пути — повторите действие; если повторяется, смотрите backend. '
    + `Технический код: REQUEST_DEADLINE_EXCEEDED (${timeoutMs} мс).`,
  );
}

export async function responseError(url: string, response: Response): Promise<Error> {
  handleApiResponse(url, response);
  return errorFromHttpResponse(url, response);
}

// Request-local provenance never goes on the wire or into a bundle. It lets
// the command owner distinguish its own contiguous CAS commits from a remote
// revision observed by a query or an unrelated no-op response.
const modelCasRequests = new WeakMap<object, { modelId: string; expected: number; authGeneration: number }>();
export function ownModelCasBase(modelId: string, payload: unknown): number | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const receipt = modelCasRequests.get(payload);
  return receipt?.modelId === modelId && receipt.authGeneration === authenticatedCacheGeneration() ? receipt.expected : undefined;
}

function bindModelCasRequest(url: string, init: RequestInit | undefined, payload: unknown): void {
  if (!payload || typeof payload !== 'object' || (init?.method ?? 'GET').toUpperCase() === 'GET') return;
  try {
    const target = new URL(url, window.location.href);
    const match = /^\/api\/models\/([^/]+)\//.exec(target.pathname);
    if (target.origin !== window.location.origin || !match) return;
    const bases: unknown[] = [];
    for (const key of ['expectedBundleVersion', 'expectedVersion']) {
      for (const value of target.searchParams.getAll(key)) bases.push(/^\d+$/.test(value) ? Number(value) : null);
    }
    if (typeof init?.body === 'string') {
      const body: unknown = JSON.parse(init.body);
      if (!body || typeof body !== 'object' || Array.isArray(body)) return;
      for (const key of ['expectedBundleVersion', 'expectedVersion']) {
        if (Object.prototype.hasOwnProperty.call(body, key)) bases.push((body as Record<string, unknown>)[key]);
      }
    } else if (init?.body != null) return;
    if (bases.length !== 1 || typeof bases[0] !== 'number' || !Number.isSafeInteger(bases[0]) || bases[0] < 0) return;
    modelCasRequests.set(payload, { modelId: decodeURIComponent(match[1]!), expected: bases[0], authGeneration: authenticatedCacheGeneration() });
  } catch {
    // Unknown body/identity forms grant no permission to advance a dirty base.
  }
}

export async function requestJson<T>(url: string, init?: RequestInit, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
  const authGeneration = authenticatedCacheGeneration();
  const bounded = boundedRequestSignal(init?.signal, timeoutMs);
  const method = (init?.method ?? 'GET').toUpperCase();
  const read = method === 'GET';
  const decode = async (response: Response): Promise<T> => {
    requireAuthenticatedGeneration(authGeneration);
    if (!response.ok) {
      throw await responseError(url, response);
    }
    const payload = await response.json() as T;
    requireAuthenticatedGeneration(authGeneration);
    bindModelCasRequest(url, init, payload);
    return payload;
  };
  let response: Response;
  try {
    try {
      response = await fetch(url, { ...init, signal: bounded.signal });
    } catch (error) {
      throw humanDeadlineRefusal(error, url, method, timeoutMs);
    }
    if (read) return await decode(response);
  } finally {
    bounded.cleanup();
  }
  // A command may already be committed once headers arrive.  Decode its body
  // without the request watchdog so response loss cannot turn into an unsafe
  // automatic retry of an ambiguous mutation.
  return decode(response);
}

